import { useEffect, useState } from "react";
import { Download, X, Share, Plus, Radio, Tv, Ticket, Check } from "lucide-react";

const DISMISS_KEY = "bbk_pwa_dismissed";        // permanent: set only once truly installed
const SESSION_KEY = "bbk_pwa_session_dismissed"; // per-visit: so we offer again on a new visit

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
}
function isIOS() {
  const ua = window.navigator.userAgent || "";
  const iDevice = /iphone|ipad|ipod/i.test(ua);
  const iPadOS = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return iDevice || iPadOS;
}

// Full-screen, standards-compliant PWA install gate shown on the FIRST visit.
// - Android/Chrome/Edge/desktop: real native install via beforeinstallprompt.
// - iPhone/iPad (no native prompt): clear Add-to-Home-Screen guidance.
// Never nags again once installed OR the customer chooses Continue to Website.
export function InstallPWA() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [mode, setMode] = useState(isIOS() ? "ios" : "android"); // android | ios | unsupported
  const [iosHelp, setIosHelp] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;                        // already installed — never show
    if (localStorage.getItem(DISMISS_KEY) === "1") return; // installed on a prior visit

    const onBIP = (e) => {
      e.preventDefault();
      setDeferred(e);
      window.__bbkInstallPrompt = e;                   // expose for an always-available trigger
      setMode("android");
      window.dispatchEvent(new Event("bbk-installable"));
      if (sessionStorage.getItem(SESSION_KEY) !== "1") setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onBIP);

    const onInstalled = () => {
      localStorage.setItem(DISMISS_KEY, "1");
      setShow(false);
    };
    window.addEventListener("appinstalled", onInstalled);

    // Lets the header/footer "Install app" entry re-open the gate at any time.
    const onOpen = () => { setIosHelp(false); setMode(isIOS() ? "ios" : (window.__bbkInstallPrompt ? "android" : "unsupported")); setShow(true); };
    window.addEventListener("bbk-open-install", onOpen);

    // Offer the gate shortly after load on each new visit (not once-forever), unless the
    // customer already dismissed it in THIS visit or the app is installed.
    const t = setTimeout(() => {
      if (isStandalone() || localStorage.getItem(DISMISS_KEY) === "1") return;
      if (sessionStorage.getItem(SESSION_KEY) === "1") return;
      setShow((cur) => {
        if (cur) return cur;
        setMode(isIOS() ? "ios" : (window.__bbkInstallPrompt ? "android" : "unsupported"));
        return true;
      });
    }, 900);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
      window.removeEventListener("bbk-open-install", onOpen);
      clearTimeout(t);
    };
  }, []);

  const dismiss = () => {
    sessionStorage.setItem(SESSION_KEY, "1");  // don't nag again THIS visit; offer again next visit
    setShow(false);
  };

  const install = async () => {
    if (mode === "ios") { setIosHelp(true); return; }
    const prompt = deferred || window.__bbkInstallPrompt;
    if (!prompt) {
      // No native prompt available on this browser — guide instead of forcing.
      if (isIOS()) { setMode("ios"); setIosHelp(true); return; }
      dismiss();
      return;
    }
    prompt.prompt();
    const choice = await prompt.userChoice.catch(() => ({ outcome: "dismissed" }));
    if (choice?.outcome === "accepted") { localStorage.setItem(DISMISS_KEY, "1"); window.__bbkInstallPrompt = null; }
    setDeferred(null);
    setShow(false);
  };

  if (!show) return null;

  const FEATURES = [
    [Radio, "Live 89.7 FM Radio"],
    [Tv, "Live TV & Match Highlights"],
    [Ticket, "Event Tickets & More"],
  ];

  return (
    <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="pwa-install-gate">
      <div className="absolute inset-0 bg-black/55" onClick={dismiss} data-testid="pwa-install-backdrop" />
      <div className="relative w-full sm:max-w-md max-h-[92vh] overflow-y-auto bg-[#0F131A] border border-[#273042] sm:rounded-3xl rounded-t-3xl shadow-2xl animate-[slideUp_.35s_ease] motion-reduce:animate-none">
        <button onClick={dismiss} data-testid="pwa-close-btn" aria-label="Close"
          className="absolute top-3.5 right-3.5 z-10 p-1.5 rounded-full bg-white/5 text-slate-400 hover:text-white hover:bg-white/10 transition">
          <X className="w-5 h-5" />
        </button>

        {/* Brand banner */}
        <div className="pt-8 pb-6 px-6 bg-gradient-to-b from-[#121a2b] to-[#0F131A] text-center border-b border-[#273042]">
          <div className="mx-auto w-20 h-20 rounded-2xl bg-white flex items-center justify-center shadow-lg ring-4 ring-[#E11D2A]/20">
            <img src="/icon-192.png" alt="B&B KIGALI FM" className="w-16 h-16 object-contain rounded-xl" />
          </div>
          <h2 className="mt-4 text-white font-extrabold text-2xl leading-tight">Install B&amp;B KIGALI FM App</h2>
          <p className="mt-1.5 text-sm text-slate-400">
            Add our app to your home screen for the fastest, fullscreen experience.
          </p>
        </div>

        {!iosHelp ? (
          <div className="p-6">
            <ul className="space-y-2.5 mb-6">
              {FEATURES.map(([Icon, label]) => (
                <li key={label} className="flex items-center gap-3 text-sm text-slate-200">
                  <span className="w-8 h-8 rounded-lg bg-[#E11D2A]/15 flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4 text-[#E11D2A]" />
                  </span>
                  {label}
                </li>
              ))}
            </ul>

            <button onClick={install} data-testid="pwa-install-btn"
              className="w-full h-12 rounded-full bg-[#E11D2A] text-white font-extrabold text-base flex items-center justify-center gap-2 hover:bg-[#C8121F] btn-glow transition">
              <Download className="w-5 h-5" />
              {mode === "ios" ? "Show me how" : "Install App"}
            </button>
            <button onClick={dismiss} data-testid="pwa-continue-btn"
              className="w-full h-11 mt-2.5 rounded-full bg-transparent text-slate-300 font-semibold text-sm hover:text-white transition">
              Continue to Website
            </button>
            {mode === "unsupported" && (
              <p className="text-[11px] text-slate-500 text-center mt-3">
                Tip: open <b className="text-slate-400">bbkigali.com</b> in Chrome or Safari to install the app.
              </p>
            )}
          </div>
        ) : (
          <div className="p-6" data-testid="pwa-ios-help">
            <p className="text-sm text-slate-300 mb-4">Install in 3 quick steps on your iPhone/iPad:</p>
            <ol className="space-y-4">
              <li className="flex items-center gap-3 text-sm text-slate-200">
                <span className="w-8 h-8 rounded-full bg-[#E11D2A] text-white font-bold flex items-center justify-center shrink-0">1</span>
                <span>Tap the <Share className="w-4 h-4 inline text-[#E11D2A] mx-0.5" /> <b>Share</b> button in the Safari toolbar</span>
              </li>
              <li className="flex items-center gap-3 text-sm text-slate-200">
                <span className="w-8 h-8 rounded-full bg-[#E11D2A] text-white font-bold flex items-center justify-center shrink-0">2</span>
                <span>Scroll and choose <Plus className="w-4 h-4 inline text-[#E11D2A] mx-0.5" /> <b>Add to Home Screen</b></span>
              </li>
              <li className="flex items-center gap-3 text-sm text-slate-200">
                <span className="w-8 h-8 rounded-full bg-[#E11D2A] text-white font-bold flex items-center justify-center shrink-0">3</span>
                <span>Tap <b>Add</b>, then open <b>B&amp;B KIGALI FM</b> from your home screen</span>
              </li>
            </ol>
            <button onClick={dismiss} data-testid="pwa-ios-done-btn"
              className="w-full h-12 mt-6 rounded-full bg-[#E11D2A] text-white font-extrabold flex items-center justify-center gap-2 hover:bg-[#C8121F] transition">
              <Check className="w-5 h-5" /> Got it
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
